import {useRouteLoaderData} from 'react-router'
import {Header, type HeaderSearchSlot} from '@core/wall/ui/Header'
import type {loader as rootLoader} from '@root'
import {UserBlock} from '@core/auth-ui/UserBlock/UserBlock'
import {Oculus} from '@product/ovid/components/Oculus/Oculus'
import styles from './header.module.scss'

export type OvidWallHeaderProps = {
    search: HeaderSearchSlot
    /** The wall's own sign-in layer, or `null` for a reader already signed in. */
    onSignIn: (() => void) | null
}

/**
 * OvidWallHeader — Ovid's own slots on the shared wall header (`@core/wall/ui/Header`) and the
 * shared user block (`@core/auth-ui/UserBlock`), plan `2026_09_17_ovid_one_wall.md`. Replaces the
 * earlier `WallHeader.tsx` fork, which duplicated the header shell and shipped a signed-in reader
 * with no account menu and no way to sign out from the wall at all.
 *
 * The brand is the estate's own mark and wordmark (`.ov-wordmark`, the point in ember), the wordmark
 * standing down on a phone so the field keeps its width.
 *
 * No nav (Ovid's pack pages are behind the surface cut). One menu link, for a subscriber only: "Manage plan", the
 * billing portal (`/stripe-portal`), where the plan is cancelled, the card changed and invoices read.
 *
 * A visitor searches like a member (the results are veiled: `wallRead.server.ts`), and the Sign in
 * door opens the wall's own sign-in layer (`onSignIn`, `Wall.tsx`'s `renderHeader` second argument)
 * rather than linking anywhere — there is nothing at `/auth/login` a signed-out visitor should land
 * on outside this same wall.
 */
export function OvidWallHeader({search, onSignIn}: OvidWallHeaderProps) {
    const hasPlan = useRouteLoaderData<typeof rootLoader>('root')?.hasPlan ?? false
    return (
        <Header
            brand={
                <>
                    <Oculus size={26} />
                    <span className={`ov-wordmark ${styles.word}`}>
                        Ovid<span className="ov-pt">.</span>
                    </span>
                </>
            }
            search={search}
            right={
                <UserBlock
                    extraLinks={hasPlan ? [{href: '/stripe-portal', label: 'Manage plan'}] : []}
                    signIn={onSignIn ? {onSignIn} : {href: '/auth/login'}}
                />
            }
        />
    )
}
